"use client";

import { getAuth } from "firebase/auth";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Consolidates auth token acquisition. When auth is disabled
 * (detected from window.__VERDEREM_CONFIG__), getToken() returns
 * null and Firebase is never touched.
 *
 * `getToken` identity is stable for the lifetime of the hook — it reads
 * `authDisabled` via a ref so we don't cascade identity churn through
 * every downstream useCallback/useEffect when the async config fetch
 * resolves on local dev (where Vite serves the HTML and the Fastify
 * config injection is absent).
 */
export function useAuthToken(): {
  getToken: (force?: boolean) => Promise<string | null>;
  fetchWithAuth: (
    input: RequestInfo | URL,
    init?: RequestInit
  ) => Promise<Response>;
  authDisabled: boolean;
  loading: boolean;
} {
  const [authDisabled, setAuthDisabled] = useState<boolean>(
    () => window.__VERDEREM_CONFIG__?.authDisabled ?? false
  );
  const [loading, setLoading] = useState(() => !window.__VERDEREM_CONFIG__);

  const authDisabledRef = useRef(authDisabled);
  authDisabledRef.current = authDisabled;

  useEffect(() => {
    if (window.__VERDEREM_CONFIG__) {
      setAuthDisabled(window.__VERDEREM_CONFIG__.authDisabled ?? false);
      setLoading(false);
      return;
    }
    fetch("/api/config")
      .then((res) => res.json())
      .then((cfg: { authDisabled?: boolean }) => {
        setAuthDisabled(cfg.authDisabled ?? false);
      })
      .catch(() => {
        /* ignore fetch errors — authDisabled defaults to false */
      })
      .finally(() => setLoading(false));
  }, []);

  const getToken = useCallback(
    async (force = false): Promise<string | null> => {
      if (authDisabledRef.current) {
        return null;
      }
      try {
        // Cached by default: `getIdToken(false)` returns the current token and
        // auto-refreshes it when it has expired (or is within ~5 min of `exp`),
        // so the happy path costs no extra network round-trip. `fetchWithAuth`
        // passes `force = true` ONLY on a 401 retry — to mint a brand-new token
        // when a cached/auto-refreshed one was still rejected (e.g. client↔kernel
        // clock skew).
        return (await getAuth().currentUser?.getIdToken(force)) ?? null;
      } catch {
        return null;
      }
    },
    []
  );

  const fetchWithAuth = useCallback(
    async (
      input: RequestInfo | URL,
      init: RequestInit = {}
    ): Promise<Response> => {
      const withToken = async (force: boolean): Promise<Response> => {
        const token = await getToken(force);
        const headers = new Headers(init.headers);
        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        }
        return fetch(input, { ...init, headers });
      };

      // First attempt uses the cached token; on a 401, retry ONCE with a
      // force-refreshed token. Do NOT loop — a persistent 401 is a real auth
      // failure the caller must surface.
      const res = await withToken(false);
      if (res.status !== 401 || authDisabledRef.current) {
        return res;
      }
      return withToken(true);
    },
    [getToken]
  );

  return { getToken, fetchWithAuth, authDisabled, loading };
}
